import { Suspense, lazy } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { Provider } from 'react-redux'
import store from '@store/store.ts'
import { hydrateCurrentUser } from '@utils/hydrator.js'
import { getCurrentUser } from '@selectors/data/user_selectors.js'
import defineAbilityFor from './ability.js'
import { AbilityContext } from './can.js'
import { setScopedFilter } from '@features/filters/index.js'
import SuspenseFallback from '@components/SuspenseFallback.tsx'
import { RedirectorContainer } from '@features/redirection/index.js'
import { FlagsProvider } from '@features/flags/FlagsContext'
import Router from './router/Routes.tsx'
import UnboundHelpButton from '@features/unbound_welcome/components/UnboundHelpButton.tsx'

const ProjectsListContainer = lazy(() =>
  import('@features/projects_list/index.js').then(module => ({ default: module.ProjectsListContainer }))
)

window.Timetoast.projects = {
  ...window.Timetoast.projects,

  index: {
    init: function (options) {
      const { databaseId } = options
      const uiScope = 'projectsIndex'

      hydrateCurrentUser()
      const currentUser = getCurrentUser(store.getState())
      const ability = defineAbilityFor(currentUser)

      // set up initial internal access level filter
      store.dispatch(
        setScopedFilter({
          uiScope,
          filterName: 'visibility',
          value: 'account',
        })
      )

      const root = createRoot(document.getElementById('react-target'))
      root.render(
        <Provider store={store}>
          <RedirectorContainer />
          <AbilityContext.Provider value={ability}>
            <Suspense fallback={<SuspenseFallback />}>
              <ProjectsListContainer databaseId={databaseId} uiScope={uiScope} />
            </Suspense>
          </AbilityContext.Provider>
        </Provider>
      )
    },
  },

  show: {
    init: function ({ tableId, isEditable }) {
      const currentUser = getCurrentUser(store.getState())
      const ability = defineAbilityFor(currentUser)
      const root = createRoot(document.getElementById('react-target'))
      root.render(
        <Provider store={store}>
          <FlagsProvider>
            <AbilityContext.Provider value={ability}>
              <BrowserRouter>
                <Router tableId={tableId} isEditable={isEditable} />
              </BrowserRouter>
            </AbilityContext.Provider>
          </FlagsProvider>
        </Provider>
      )

      // Mount the Help button into the slim header if the container exists
      const helpMount = document.getElementById('js-help-mount')
      if (helpMount) {
        const helpRoot = createRoot(helpMount)
        helpRoot.render(<UnboundHelpButton />)
      }
    },
  },
}
